import React, { Fragment } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Button } from "react-bootstrap";
import { Typography } from "@mui/material";
import { MARKETPLACE_CONSTANTS } from "../../../../../config/constants";
import { colors } from "../../../../../config/colors";
import { TEXT_ALIGN_LEFT } from "../../../../../config/styles";
import {
  SideBarAddLogo,
  ProductSwapLogo,
  PlainEditLogo,
  PlainDeleteLogo,
} from "../../../../../assets/icons";
import OverrideForm from "./OverrideForm";

/**
 * OverrideList Component
 *
 * Displays a list of product overrides with drag-and-drop reordering capability.
 * Allows adding, editing, and deleting overrides.
 */
const OverrideList = ({
  overrideList,
  currency,
  gradesDivisionList,
  gradeList,
  merchantId,
  editOverrideIndex,
  showOverrideForm,
  onOverrideListChange,
  onEditOverrideIndexChange,
  onShowOverrideFormChange,
  onDeleteOverride,
}) => {
  // Handle drag end for reordering
  const onOverrideDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(overrideList);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    onOverrideListChange(items);
  };

  // Handle save override (add or edit)
  const handleSaveOverride = (overrideDetails, index) => {
    if (index !== undefined && index !== null) {
      // Edit existing
      const updatedList = [...overrideList];
      updatedList[index] = overrideDetails;
      onOverrideListChange(updatedList);
      onEditOverrideIndexChange(null);
    } else {
      // Add new
      onOverrideListChange([...overrideList, overrideDetails]);
      onShowOverrideFormChange(false);
    }
  };

  return (
    <div className="configuration-page">
      <div className="d-flex justify-content-between align-items-center configuration-general">
        <Typography variant="h5" className="d-flex align-items-center">
          {MARKETPLACE_CONSTANTS.OVERRIDE}
        </Typography>
        <Button
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
          }}
          className="d-flex align-items-center gap-2"
          onClick={() => onShowOverrideFormChange(!showOverrideForm)}
        >
          <SideBarAddLogo bgColor={colors.primary} />
          <Typography
            variant="body1"
            sx={{ color: colors.primary }}
            component="span"
          >
            {MARKETPLACE_CONSTANTS.ADD_OVERRIDE || "Add Override"}
          </Typography>
        </Button>
      </div>

      {/* Override List with Drag & Drop */}
      <DragDropContext onDragEnd={onOverrideDragEnd}>
        <Droppable droppableId="overrides">
          {(provided) => (
            <div
              className="d-flex flex-column gap-1 w-100"
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {overrideList.map((override, idx) => (
                <Draggable
                  key={idx}
                  draggableId={`override-${idx}`}
                  index={idx}
                >
                  {(provided) => (
                    <Fragment>
                      <div
                        className="specification-wrapper"
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                      >
                        <div className="d-flex gap-3 w-75 align-items-center">
                          <Button
                            style={{
                              background: "none",
                              border: "none",
                              padding: 0,
                              display: "flex",
                              cursor: "grab",
                            }}
                          >
                            <ProductSwapLogo />
                          </Button>
                          <div>
                            <Typography
                              variant="subtitle2"
                              sx={{ textAlign: TEXT_ALIGN_LEFT }}
                            >
                              {Array.isArray(override.recipients)
                                ? override.recipients.join(", ")
                                : override.recipients}
                            </Typography>
                            {override.price && (
                              <Typography
                                variant="h4"
                                sx={{
                                  textAlign: TEXT_ALIGN_LEFT,
                                  color: colors.grayLight,
                                }}
                              >
                                Price: {override.price} {currency}
                              </Typography>
                            )}
                          </div>
                        </div>
                        <div className="d-flex w-25 align-items-start justify-content-end gap-3">
                          <Button
                            style={{
                              background: "none",
                              border: "none",
                              padding: 0,
                            }}
                            onClick={() => {
                              onShowOverrideFormChange(false);
                              onEditOverrideIndexChange(
                                editOverrideIndex === idx ? null : idx
                              );
                            }}
                          >
                            <PlainEditLogo />
                          </Button>
                          <Button
                            style={{
                              background: "none",
                              border: "none",
                              padding: 0,
                            }}
                            onClick={() => onDeleteOverride(idx)}
                          >
                            <PlainDeleteLogo />
                          </Button>
                        </div>
                      </div>

                      {/* Edit Form */}
                      {editOverrideIndex === idx && (
                        <OverrideForm
                          initialValues={override}
                          onSave={(overrideDetails) =>
                            handleSaveOverride(overrideDetails, idx)
                          }
                          onDelete={() => onEditOverrideIndexChange(null)}
                          currency={currency}
                          gradesDivisionList={gradesDivisionList}
                          gradeList={gradeList}
                          merchantId={merchantId}
                        />
                      )}
                    </Fragment>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* Add New Override Form */}
      {showOverrideForm && (
        <OverrideForm
          onSave={handleSaveOverride}
          onDelete={() => onShowOverrideFormChange(false)}
          currency={currency}
          gradesDivisionList={gradesDivisionList}
          gradeList={gradeList}
          merchantId={merchantId}
        />
      )}
    </div>
  );
};

export default OverrideList;
